/**
 * TemplateListItem Component
 *
 * Individual template item in the sidebar list.
 * Supports drag-and-drop reordering using @dnd-kit and displays template ownership indicators.
 */

import React from 'react';
import Logo from '@components/Icons/Logo';
import { useThemeContext } from '@context/theme/context';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AccountCircle, Groups } from '@mui/icons-material';
import { Box, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { TemplateWithMeta } from '../types';

import { DragHandle } from './primitives/DragHandle';

interface TemplateListItemProps {
  /** Template data with metadata */
  template: TemplateWithMeta;
  /** Whether this template is selected */
  isSelected: boolean;
  /** Click handler */
  onClick: () => void;
}

export const TemplateListItem: React.FC<TemplateListItemProps> = ({
  template,
  isSelected,
  onClick,
}) => {
  const theme = useTheme();
  const { brandLanguage } = useThemeContext();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: template.template_id,
    animateLayoutChanges: () => false, // Disable layout animations for better performance
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
  };

  // Get the appropriate icon and tooltip based on template type
  const getTemplateIcon = () => {
    switch (template.iconType) {
      case 'user':
        return (
          <Tooltip
            title="Your template - fully customizable"
            placement="top"
            arrow
          >
            <AccountCircle
              sx={{
                fontSize: 20,
                color: isSelected ? 'primary.main' : 'text.secondary',
              }}
            />
          </Tooltip>
        );
      case 'group':
        return (
          <Tooltip
            title="Group template - only group admins can edit"
            placement="top"
            arrow
          >
            <Groups
              sx={{
                fontSize: 20,
                color: isSelected ? 'primary.main' : 'text.secondary',
              }}
            />
          </Tooltip>
        );
      case 'public':
        return (
          <Tooltip
            title={`${brandLanguage.brandName} template - duplicate to customize`}
            placement="top"
            arrow
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Logo size={18} />
            </Box>
          </Tooltip>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        bgcolor: isSelected
          ? 'action.selected'
          : isDragging
            ? 'action.hover'
            : 'background.paper',
        opacity: isDragging ? 0.5 : 1,
        transition: theme.transitions.create(
          ['border-color', 'background-color', 'box-shadow'],
          { duration: theme.transitions.duration.short },
        ),
        '&:hover': {
          bgcolor: isSelected ? 'action.selected' : 'action.hover',
          borderColor: isSelected ? 'primary.main' : 'primary.light',
        },
        ...(isDragging && {
          boxShadow: theme.shadows[8],
        }),
      }}
    >
      {/* Drag Handle */}
      <Box sx={{ ml: -0.5 }}>
        <DragHandle listeners={listeners} attributes={attributes} size="small" />
      </Box>

      {/* Template Name */}
      <Typography
        variant="body2"
        fontWeight={isSelected ? 600 : 500}
        sx={{
          flex: 1,
          color: isSelected ? 'primary.main' : 'text.primary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {template.display_name}
      </Typography>

      {/* Template Type Icon */}
      {getTemplateIcon()}
    </Box>
  );
};
